
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const AdLeadForm: React.FC = () => {
  const { toast } = useToast();
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    helpWith: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormState(prev => ({ ...prev, helpWith: value }));
  };

  const validateForm = () => {
    if (!formState.name) return "Name is required";
    if (!formState.email) return "Email is required";
    if (!formState.email.includes('@')) return "Please enter a valid email address";
    if (!formState.helpWith) return "Please select what you need help with";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const error = validateForm();
    if (error) {
      setFormError(error);
      return;
    }
    
    setIsSubmitting(true);
    setFormError(null);
    
    try {
      const form = e.currentTarget;
      const formData = new FormData(form);
      
      const response = await fetch(form.action, {
        method: form.method,
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      
      if (response.ok) {
        toast({
          title: "Thanks for reaching out!",
          description: "We've received your request and will send your plan shortly.",
        });
        
        setFormState({
          name: '',
          email: '',
          helpWith: '',
          message: ''
        });
      } else {
        const data = await response.json();
        if (data.errors) {
          setFormError(data.errors.map((error: any) => error.message).join(", "));
        } else {
          setFormError("There was a problem submitting your request. Please try again.");
        }
      }
    } catch (error) {
      console.error('Error sending form:', error);
      setFormError("There was a problem submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="lead-form" className="py-20 px-6 md:px-10 bg-gradient-to-b from-blue-50/50 to-indigo-50/30 dark:from-gray-800/50 dark:to-gray-800/30">
      <div className="container mx-auto max-w-3xl">
        <div className="text-center mb-10 animate-fade-in-up opacity-0" style={{animationDelay: "200ms", animationFillMode: "forwards"}}>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Tell Us Where You Want Results
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            We'll send back a simple plan—what to build, how it works, and why it makes sense.
          </p>
        </div>
        
        <Card className="p-8 border border-gray-200 dark:border-gray-700 shadow-md bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm animate-fade-in-up opacity-0 transition-all duration-500" style={{animationDelay: "400ms", animationFillMode: "forwards"}}>
          {formError && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          
          <form 
            onSubmit={handleSubmit} 
            action="https://formspree.io/f/xwplpoyz" 
            method="POST"
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="animate-fade-in-up opacity-0" style={{animationDelay: "500ms", animationFillMode: "forwards"}}>
                <Label htmlFor="name">Your Name</Label>
                <Input 
                  id="name"
                  name="name"
                  placeholder="First name is fine" 
                  value={formState.name}
                  onChange={handleChange}
                  required
                  className="bg-white dark:bg-gray-900 focus:border-heading-accent transition-all duration-300 focus:ring-2 focus:ring-heading-accent/30"
                />
              </div>
              
              <div className="animate-fade-in-up opacity-0" style={{animationDelay: "600ms", animationFillMode: "forwards"}}>
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email"
                  name="email"
                  type="email"
                  placeholder="We'll only use this to send your plan—no spam"
                  value={formState.email}
                  onChange={handleChange}
                  required
                  className="bg-white dark:bg-gray-900 focus:border-heading-accent transition-all duration-300 focus:ring-2 focus:ring-heading-accent/30"
                />
              </div>
              
              <div className="animate-fade-in-up opacity-0" style={{animationDelay: "700ms", animationFillMode: "forwards"}}>
                <Label htmlFor="helpWith">What do you want help with?</Label>
                <input type="hidden" name="Help With" value={formState.helpWith} />
                <Select onValueChange={handleSelectChange} value={formState.helpWith} required>
                  <SelectTrigger className="bg-white dark:bg-gray-900 transition-all duration-300 focus:ring-2 focus:ring-heading-accent/30">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="getting-started">I need help figuring out where to start</SelectItem>
                    <SelectItem value="ai-agents">I want an AI sales agent or avatar</SelectItem>
                    <SelectItem value="tools">I need tools to save time or reduce admin</SelectItem>
                    <SelectItem value="data">I want better data, dashboards, or insights</SelectItem>
                    <SelectItem value="other">Something else (I'll explain below)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="animate-fade-in-up opacity-0" style={{animationDelay: "800ms", animationFillMode: "forwards"}}>
                <Label htmlFor="message">
                  Anything else you'd like us to know? <span className="text-sm text-muted-foreground">(optional)</span>
                </Label>
                <Textarea 
                  id="message"
                  name="message"
                  placeholder="Totally optional—but helpful"
                  value={formState.message}
                  onChange={handleChange}
                  className="min-h-[100px] bg-white dark:bg-gray-900 focus:border-heading-accent transition-all duration-300 focus:ring-2 focus:ring-heading-accent/30"
                />
              </div>
            </div>
            
            {/* Hidden fields for additional information */}
            <input type="hidden" name="Request Type" value="landing-page-lead" />
            <input type="hidden" name="Email to" value="scott@sparkwave-ai.com" />
            <input type="hidden" name="_subject" value="Sparkwave AI: New Landing Page Lead" />
            <input type="hidden" name="_autoresponse" value="Thank you for contacting Sparkwave AI. We've received your message and will get back to you shortly with your plan." />
            
            <Button 
              type="submit" 
              className="w-full bg-heading-accent hover:bg-heading-accent/90 text-white transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] animate-fade-in-up opacity-0"
              style={{animationDelay: "900ms", animationFillMode: "forwards"}}
              disabled={isSubmitting}
              size="lg"
            >
              {isSubmitting ? "Sending..." : "Send Me My Plan →"}
            </Button>
            
            <div className="flex items-center text-sm text-muted-foreground pt-2 animate-fade-in-up opacity-0" style={{animationDelay: "1000ms", animationFillMode: "forwards"}}>
              <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              <span>We respect your privacy and will never share your information.</span>
            </div>
          </form>
        </Card>
      </div>
    </section>
  );
};

export default AdLeadForm;
